import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  fetchPriceList,
  fetchPriceListItem,
  createPriceListItem,
  updatePriceListItem,
  deletePriceListItem,
  approvePriceListItem,
  rejectPriceListItem,
  FetchPriceListParams,
  PriceListPayload,
  PriceListResponse,
} from "../../services/priceListService";

/**
 * Query hook for price list catalog and analytics (Admin & SuperAdmin)
 */
export const usePriceListQuery = (params?: FetchPriceListParams) => {
  return useQuery<PriceListResponse>({
    queryKey: queryKeys.priceList.list(params?.category, params?.search),
    queryFn: ({ signal }) => fetchPriceList(params, signal),
  });
};

/**
 * Query hook for a single price list item
 */
export const usePriceListItemQuery = (id?: number) => {
  return useQuery({
    queryKey: [...queryKeys.priceList.all, "item", id],
    queryFn: () => fetchPriceListItem(id!),
    enabled: typeof id === "number",
  });
};

/**
 * Mutation hook to create price list item
 */
export const useCreatePriceListItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PriceListPayload) => createPriceListItem(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceList.all });
    },
  });
};

/**
 * Mutation hook to update price list item
 */
export const useUpdatePriceListItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<PriceListPayload> }) =>
      updatePriceListItem(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceList.all });
    },
  });
};

/**
 * Mutation hook to delete price list item
 */
export const useDeletePriceListItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deletePriceListItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceList.all });
    },
  });
};

/**
 * Mutation hook to approve pending price item
 */
export const useApprovePriceListItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => approvePriceListItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceList.all });
    },
  });
};

/**
 * Mutation hook to reject pending price item
 */
export const useRejectPriceListItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      rejectPriceListItem(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceList.all });
    },
  });
};
